import { Suspense, lazy, type ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { AppShell } from "@/components/AppShell";
import { SlowNotice } from "@/components/SlowNotice";
import { EnvironmentBanner } from "@/components/EnvironmentBanner";
import { SignIn } from "@/routes/SignIn";
import { Dashboard } from "@/routes/Dashboard";

/**
 * Screens are code-split.
 *
 * The dashboard ships in the entry chunk because it is where every session
 * starts; everything else loads on navigation. Review in particular drags in
 * the markdown renderer, and an operator who only ever looks at the pipeline
 * should not pay for it.
 */
const Pipeline = lazy(() =>
  import("@/routes/Pipeline").then((m) => ({ default: m.Pipeline })),
);
const Review = lazy(() =>
  import("@/routes/Review").then((m) => ({ default: m.Review })),
);
const Strategies = lazy(() =>
  import("@/routes/Strategies").then((m) => ({ default: m.Strategies })),
);
const Runs = lazy(() => import("@/routes/Runs").then((m) => ({ default: m.Runs })));
const RunDetail = lazy(() =>
  import("@/routes/RunDetail").then((m) => ({ default: m.RunDetail })),
);
const Settings = lazy(() =>
  import("@/routes/Settings").then((m) => ({ default: m.Settings })),
);

function RouteFallback() {
  return (
    <div className="flex h-64 flex-col items-center justify-center gap-3">
      <Loader2 className="size-5 animate-spin text-muted-foreground" />
      {/* Chunk fetches are usually instant from cache; when they are not, the
          screen is blank and silent, which is the worst version of slow. */}
      <SlowNotice active message="Still loading this screen…" />
    </div>
  );
}

/**
 * Everything sits under the environment banner, signed in or not — the moment
 * you are most likely to be looking at the wrong tab is the sign-in screen.
 */
function Frame({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-full flex-col">
      <EnvironmentBanner />
      <div className="min-h-0 flex-1">{children}</div>
    </div>
  );
}

export function App() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Checking your access…</p>
        <SlowNotice
          active
          message="Taking longer than usual — still checking."
        />
      </div>
    );
  }

  if (!user) {
    return (
      <Frame>
        <SignIn />
      </Frame>
    );
  }

  return (
    <Frame>
      <AppShell>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/pipeline" element={<Pipeline />} />
            <Route path="/review" element={<Review />} />
            <Route path="/strategies" element={<Strategies />} />
            <Route path="/runs" element={<Runs />} />
            <Route path="/runs/:id" element={<RunDetail />} />
            <Route path="/settings" element={<Settings />} />
            {/* Unknown paths bounce home rather than dead-ending — the Worker
                already serves index.html for any non-API route. */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </AppShell>
    </Frame>
  );
}
